(ns client-portal.client.voice-insights.sessions.charts.views
  (:require [shared.core.amelia-light.components.components :as alc]
            [shared.core.constants :as constants]
            [clojure.string :as s]
            [shared.core.util :as util]
            [shared.core.reactive :as re]
            [client-portal.client.vi.components.tag :as tag]
            [shared.core.amelia-light.components.card :as card]
            [client-portal.client.voice-insights.sessions.data :as sessions.d]
            [shared.core.router :as router]
            [client-portal.client.voice-insights.sessions.charts.data :as charts.data]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [client-portal.client.voice-insights.charts.views :as vi.charts]
            [shared.core.components.recharts-components :as recharts.c]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.style :as style]
            [client-portal.client.layout :as l]))

(def stack-key-separator #"\.")

(defn stack-names [tag-keys]
  (->> tag-keys
       (map #(-> % str (s/split stack-key-separator) first))
       distinct))

(defn stack-colors [names]
  (let [colors (mapv #(get charts.data/tag-category-colors % "#cccccc") names)]
    (if (seq colors) colors ["#cccccc"])))

(defn tags-sessions-toggle [{:keys [selection*]}]
  (let [selection* (or selection* (re/ratom "tags"))]
    (fn [{:keys [style]}]
      [alc/up-toggle-group {:type "single"
                            :value @selection*
                            :on-value-change (fn [v]
                                               (when (seq v)
                                                 (reset! selection* v)))
                            :items [{:value "tags" :content "By Tags"}
                                    {:value "sessions" :content "By Sessions"}]
                            :style style}])))

(defn tag-distribution-by-day-chart [_]
  (let [grouping-form-path sessions.d/charts-settings-path
        grouping-form-name :tag-distribution-by-day-grouping-value
        selected-grouping- (re/subscribe [:form/val- grouping-form-path grouping-form-name])
        tags-sessions* (re/ratom "tags")]
    (fn [chart-data]
      (let [sessions-pct? (= @tags-sessions* "sessions")
            current-grouping (or @selected-grouping- "total")
            prep-dist-data (fn [d]
                             (->> d
                                  (mapv (fn [{:keys [tag-counts] :as i}]
                                          (merge (dissoc i :tag-counts)
                                                 (->> tag-counts
                                                      (filterv charts.data/utterance-tag-name-allowed-filter)
                                                      (mapv (fn [tc]
                                                              [(str (:name tc) "." (:value tc)) (:count tc)]))
                                                      (into {})))))))
            sort-by-time-value (fn [items] (sort-by #(some-> % :time-value (util/parse-int)) items))
            chart-data (if sessions-pct?
                         (condp = current-grouping
                           "daily" (-> chart-data :by-session :daily :data (prep-dist-data) (sort-by-time-value))
                           "hourly" (-> chart-data :by-session :hourly :data (prep-dist-data) (sort-by-time-value))
                           "by-day-of-week" (-> chart-data :by-session :day-of-week :data (prep-dist-data) (sort-by-time-value))
                           (-> chart-data :by-session :all :data (prep-dist-data)))
                         (condp = current-grouping
                           "daily" (-> chart-data :by-tag :daily :data (prep-dist-data) (sort-by-time-value))
                           "hourly" (-> chart-data :by-tag :hourly :data (prep-dist-data) (sort-by-time-value))
                           "by-day-of-week" (-> chart-data :by-tag :day-of-week :data (prep-dist-data) (sort-by-time-value))
                           (-> chart-data :by-tag :all :data (prep-dist-data))))
            tag-keys (charts.data/get-all-utterance-tag-keys chart-data)
            names (stack-names tag-keys)
            colors (stack-colors names)]
        [card/card {:title "Tag Distribution"
                    :icon-type :TagsIcon}
         [:div {:style {:display :flex
                        :flex-wrap :wrap
                        :align-items :flex-start
                        :justify-content :space-between
                        :gap 8}}
          [alc/pill-button-group {:selected-value current-grouping
                                  :form-path grouping-form-path
                                  :form-name grouping-form-name
                                  :options [{:label "Total" :value "total"}
                                            {:label "Day of Week" :value "by-day-of-week"}
                                            {:label "Daily" :value "daily"}
                                            {:label "Hourly" :value "hourly"}]}]
          [tags-sessions-toggle {:selection* tags-sessions*}]]
         [recharts.c/stacked-bar-chart
          (cond-> {:chart-data chart-data
                   :data-keys tag-keys
                   :stack-separator stack-key-separator
                   :tooltip-label-transf-fn (fn [v] (tag/tag->display-name {:value v}))
                   :tooltip-show-percent? true
                   :colors colors
                   :chart-height 550
                   :show-legend true
                   :use-gradients true
                   :time-grouping current-grouping
                   :chart-container-style {:align-items :center}
                   :chart-style {:width :auto
                                 :max-width "100%"}}
                  sessions-pct?
                  (assoc :y-axis? false
                         :tooltip-value-transf-fn (fn [v] (str v "%"))
                         :tooltip-show-percent? false))]]))))

(defn overall-tag-distribution-chart [raw-conversations]
  (let [pie-data (charts.data/analyze-utterance-tags-for-pie raw-conversations)
        {:keys [inner-data outer-data]} pie-data]
    (when (and (seq inner-data) (seq outer-data))
      [card/card {:title "Overall Tag Distribution"
                  :icon-type :TagsIcon}
       [recharts.c/nested-pie-chart
        {:inner-data inner-data
         :outer-data outer-data
         :width 700
         :height 700
         :show-center-label? true
         :show-legend? true}]])))

(defn tag-distribution-by-day-of-week-chart [_]
  (let [grouping-form-path sessions.d/charts-settings-path
        grouping-form-name :tag-distribution-by-day-of-week-grouping-value
        selected-grouping- (re/subscribe [:form/val- grouping-form-path grouping-form-name])
        tags-sessions* (re/ratom "tags")]
    (fn [raw-conversations]
      (let [sessions-pct? (= @tags-sessions* "sessions")
            selected-grouping (or @selected-grouping- "top-20")
            chart-data (if sessions-pct? (:by-session raw-conversations) (:by-tag raw-conversations))
            {:keys [nodes links]} (charts.data/prepare-sankey-data-for-day-of-week selected-grouping chart-data)]
        (when (and (seq nodes) (seq links))
          [card/card {:title "Tag Distribution by Day of Week"
                      :icon-type :TagsIcon}
           [:div {:style {:display :flex
                          :flex-wrap :wrap
                          :align-items :flex-start
                          :justify-content :space-between
                          :gap 8}}
            [alc/pill-button-group {:selected-value selected-grouping
                                    :form-path grouping-form-path
                                    :form-name grouping-form-name
                                    :options [{:label "Top 20" :value "top-20"}
                                              {:label "All Tags" :value "all"}]}]
            [tags-sessions-toggle {:selection* tags-sessions*}]]
           [recharts.c/sankey-chart
            {:nodes nodes
             :links links
             :tooltip-config (cond-> {:sort-by-value? true
                                      :custom-sort (fn [a b]
                                                     (if (some-> a :key (s/starts-with? "outgoing-"))
                                                       (> (some-> a :value (util/parse-int))
                                                          (some-> b :value (util/parse-int)))
                                                       (let [dow-int #(or (util/index-of (:label %) constants/days-of-the-week)
                                                                          99)
                                                             a-val (dow-int a)
                                                             b-val (dow-int b)]
                                                         (< a-val b-val))))
                                      :limit 20}
                                     sessions-pct?
                                     (assoc :value-transf-fn (fn [v] (str v "%"))
                                            :show-percent? false))
             :node-padding 20
             :margin {:left 100 :right 340 :top 20 :bottom 20}}]])))))

(defn session-count-chart [_]
  (let [grouping-form-path sessions.d/charts-settings-path
        time-period-form-name :session-count-time-period-value
        selected-time-period- (re/subscribe [:form/val- grouping-form-path time-period-form-name])]
    (fn [all-charts-data]
      (when (:session-count (util/when-not-loading all-charts-data))
        (let [selected-time-period @selected-time-period-
              selected-time-period (or selected-time-period "all")
              all-data (some-> (util/when-not-loading all-charts-data) :session-count)
              time-period-key (case selected-time-period
                                "hourly" :hour
                                "daily" :day
                                "day-of-week" :day-of-week
                                :hour)
              grouping-keyword (keyword selected-time-period)
              chart-data (some->> (get all-data grouping-keyword)
                                  :data
                                  (mapv (fn [r]
                                          {:name (if (= selected-time-period "all")
                                                   "Total"
                                                   (get r time-period-key))
                                           :values [{:key "Total"
                                                     :value (:count r)
                                                     :record r}]})))
              chart-keys-config {"Total" {:fill "#B2C4FC"}}
              data? (or (util/loading? all-charts-data)
                        (if (= selected-time-period "all")
                          (-> chart-data (first) :values (first) :record :count (not= 0))
                          (not-empty chart-data)))]
          [card/card {:title "Session Count"
                      :material-icon-type :forum}
           (when data?
             [alc/pill-button-group {:selected-value selected-time-period
                                     :form-path grouping-form-path
                                     :form-name time-period-form-name
                                     :options [{:label "Total" :value "all"}
                                               {:label "Day of Week" :value "day-of-week"}
                                               {:label "Daily" :value "daily"}
                                               {:label "Hourly" :value "hourly"}]}])
           (if data?
             [recharts.c/bar-chart
              {:data chart-data
               :data-keys-config chart-keys-config
               :legend? false
               :margin {:top 20 :right 0 :left 0 :bottom -50}
               :chart-height 500
               :x-axis-config {:angle -45
                               :height 150}
               :tooltip-config {:header-transf-name-fn identity
                                :label-transf-fn identity
                                :show-percent? false
                                :show-double-click-message? false
                                :style-map {:max-width 240}}}]
             [empty-state/no-chart-data {}])])))))

(defn intent-resolution-chart [_]
  (let [grouping-form-path sessions.d/charts-settings-path
        grouping-form-name :intent-resolution-grouping-value
        selected-grouping- (re/subscribe [:form/val- grouping-form-path grouping-form-name])]
    (fn [{:keys [intent-resolution-sankey]}]
      (when intent-resolution-sankey
        (let [{:keys [all top-10 top-10-keys]} intent-resolution-sankey
              selected-grouping (or @selected-grouping- "top-10")
              top-10? (= selected-grouping "top-10")
              {:keys [nodes links]} (if top-10? top-10 all)
              data? (and (not-empty nodes) (not-empty links))]
          [card/card {:title "Intent Resolution"
                      :icon-type :CallIntentIcon}
           (when (and data? (>= (count top-10-keys) 10))
             [alc/pill-button-group {:selected-value selected-grouping
                                     :form-path grouping-form-path
                                     :form-name grouping-form-name
                                     :options [{:label "Top 10" :value "top-10"}
                                               {:label "All Intents" :value "all"}]}])
           (if data?
             [recharts.c/sankey-chart
              {:nodes nodes
               :links links
               :tooltip-config {:sort-by-value? true
                                :limit 20
                                :show-double-click-message? false}
               :node-padding 20
               :margin {:left 340 :right 200 :top 20 :bottom 20}}]
             [empty-state/no-chart-data {}])])))))

(defn intent-resolution-rate-chart [{:keys [intent-resolution-rate] :as d}]
  (when intent-resolution-rate
    (let [data? (not-empty intent-resolution-rate)]
      [card/card {:title "Intent Resolution Rate"
                  :icon-type :CallIntentIcon}
       (if data?
         [recharts.c/donut-chart
          {:data (reverse (sort-by :value intent-resolution-rate))
           :width 500
           :height 500
           :inner-radius 60
           :outer-radius 100
           :show-center-label? true
           :show-legend? true
           :show-labels? true
           :tooltip? false}]
         [empty-state/no-chart-data {}])])))

(defn intent-sentiment-sankey [_]
  (let [grouping-form-path sessions.d/charts-settings-path
        grouping-form-name :intent-sentiment-grouping-value
        selected-grouping- (re/subscribe [:form/val- grouping-form-path grouping-form-name])]
    (fn [{:keys [intent-summary-by-sentiment]}]
      (when intent-summary-by-sentiment
        (let [top-10-keys (some->> intent-summary-by-sentiment
                                   (group-by :value)
                                   (mapv (fn [[value vals]]
                                           {:value value
                                            :count (apply + (map :count vals))}))
                                   (sort-by :count)
                                   (reverse)
                                   (take 10)
                                   (map :value)
                                   (into #{}))
              selected-grouping (or @selected-grouping- "top-10")
              top-10? (= selected-grouping "top-10")
              data? (not-empty intent-summary-by-sentiment)
              chart-data (some->> intent-summary-by-sentiment
                                  (#(if (and top-10? top-10-keys)
                                      (filterv (fn [r] (contains? top-10-keys (:value r))) %)
                                      %))
                                  (hash-map :source-key :value
                                            :source-colors recharts.util/color-from-string
                                            :sort-sources (fn [a b]
                                                            (compare (:value a) (:value b)))
                                            :target-key :child-value
                                            :sort-targets (fn [a b]
                                                            (compare (:child-value a) (:child-value b)))
                                            :target-colors (fn [in-s]
                                                             (or (get {"Negative" "#F5B7B1"
                                                                       "Neutral" "#F9D99D"
                                                                       "Positive" "#6DD9BD"}
                                                                      in-s)
                                                                 (recharts.util/color-from-string in-s)))
                                            :value-key :count
                                            :data)
                                  (recharts.util/build-sankey-data))]
          [card/card {:title "Intent Sentiment"
                      :icon-type :CallIntentIcon}
           [:div {:style {:display :flex
                          :flex-direction :column
                          :gap 12}}
            (when (and data? (>= (count top-10-keys) 10))
              [alc/pill-button-group {:selected-value selected-grouping
                                      :form-path grouping-form-path
                                      :form-name grouping-form-name
                                      :options [{:label "Top 10" :value "top-10"}
                                                {:label "All Intents" :value "all"}]}])]
           (if (not-empty intent-summary-by-sentiment)
             [recharts.c/sankey-chart
              {:nodes (:nodes chart-data)
               :links (:links chart-data)
               :tooltip-config {:sort-by-value? true
                                :limit 20
                                :show-double-click-message? false}
               :node-padding 20
               :margin {:left 340 :right 140 :top 20 :bottom 20}}]
             [empty-state/no-chart-data {}])])))))

(defn intent-sentiment-flow-chart [_]
  (let [grouping-form-path sessions.d/charts-settings-path
        grouping-form-name :intent-sentiment-flow-grouping-value
        selected-grouping- (re/subscribe [:form/val- grouping-form-path grouping-form-name])]
    (fn [{:keys [intent-sentiment-flow]}]
      (when intent-sentiment-flow
        (let [data? (not-empty intent-sentiment-flow)
              selected-grouping (or @selected-grouping- "top-10")
              top-10-keys (when data?
                            (some->> intent-sentiment-flow
                                     (group-by :value)
                                     (mapv (fn [[value vals]]
                                             {:value value
                                              :count (apply + (map :count vals))}))
                                     (sort-by :count)
                                     (reverse)
                                     (mapv :value)
                                     (take 10)
                                     (into #{})))]
          [card/card {:title "Intent Sentiment Flow"
                      :icon-type :CallIntentIcon}
           [:div {:style {:display :flex
                          :flex-direction :column
                          :gap 12}}
            (when (and data? (>= (count top-10-keys) 10))
              [alc/pill-button-group {:selected-value selected-grouping
                                      :form-path grouping-form-path
                                      :form-name grouping-form-name
                                      :options [{:label "Top 10" :value "top-10"}
                                                {:label "All Intents" :value "all"}]}])]
           (if data?
             (let [intent-sentiment-flow (if (= selected-grouping "top-10")
                                           (filterv #(contains? top-10-keys (:value %)) intent-sentiment-flow)
                                           intent-sentiment-flow)
                   value-node-lookups (->> intent-sentiment-flow
                                           (mapv :value)
                                           (distinct)
                                           (sort)
                                           (map-indexed (fn [idx value]
                                                          [value idx]))
                                           (into {}))
                   value-nodes (mapv (fn [[value _]]
                                       (let [n (tag/tag->display-name {:value value})]
                                         {:name n
                                          :isSource true
                                          :color (recharts.util/color-from-string n)
                                          :width 10
                                          :stroke (style/alc-color "Background/card_hex")
                                          :strokeWidth 1
                                          :opacity 1.0}))
                                     (sort-by first value-node-lookups))
                   sentiment-flow-node-lookups (->> intent-sentiment-flow
                                                    (mapv :sentiment-flow)
                                                    (distinct)
                                                    (sort)
                                                    (map-indexed (fn [idx value]
                                                                   [value (+ idx (count value-nodes))]))
                                                    (into {}))
                   sentiment-flow-nodes (mapv (fn [[f _]]
                                                {:name (util/capitalize-words f)
                                                 :isSource false
                                                 :color (recharts.util/color-from-string f)
                                                 :width 10
                                                 :stroke (style/alc-color "Background/card_hex")
                                                 :strokeWidth 1
                                                 :opacity 1.0})
                                              (sort-by first sentiment-flow-node-lookups))
                   nodes (vec (concat value-nodes sentiment-flow-nodes))
                   links (->> intent-sentiment-flow
                              (mapv (fn [{:keys [value sentiment-flow count]}]
                                      {:source (get value-node-lookups value)
                                       :target (get sentiment-flow-node-lookups sentiment-flow)
                                       :value count})))]
               [recharts.c/sankey-chart
                {:nodes nodes
                 :links links
                 :tooltip-config {:sort-by-value? true
                                  :limit 20
                                  :show-double-click-message? false}
                 :node-padding 20
                 :margin {:left 340 :right 180 :top 20 :bottom 20}}])
             [empty-state/no-chart-data {}])])))))

(defn improvement-recommendations-chart [_]
  (let [sessions-query-params- (re/subscribe [:sessions/relevant-query-params-])]
    (fn [{:keys [improvement-recommendations]}]
      (when improvement-recommendations
        (let [data? (not-empty improvement-recommendations)]
          [card/card {:title "Improvement Recommendations"
                      :material-icon-type :lightbulb}
           (if data?
             [recharts.c/donut-chart
              {:data (->> (reverse (sort-by :value improvement-recommendations))
                          (mapv (fn [r]
                                  (assoc r :on-double-click
                                           (fn [e {:keys [raw]}]
                                             (router/open-route-based-on-event
                                               e
                                               [:vi/sessions
                                                nil
                                                (assoc @sessions-query-params-
                                                  :fd (sessions.d/filters->query-param
                                                        [{:by "tags"
                                                          :condition "include"
                                                          :join "or"
                                                          :value [{:name "improvement-recommendation"
                                                                   :value (str (:value raw))}]}]))]
                                               :on-route-change #(re/dispatch [:sessions/reload-conversations-list])))))))
               :width 500
               :height 500
               :inner-radius 60
               :outer-radius 100
               :show-center-label? true
               :show-legend? true
               :show-labels? true
               :tooltip-config {:show-double-click-message? true}}]
             [empty-state/no-chart-data {}])])))))

(defn charts-view []
  (let [tags @(re/subscribe [:vi-tags/list-by-current-concept-id-])
        charts-data @(re/subscribe [:sessions/charts-list-])
        chart-ids-loading? @(re/subscribe [:voice-insights/charts-loading?- ["intents-by-value-counts"
                                                                             "churn-signal-by-value"]])]
    (cond
      (or (util/nil-or-loading? tags) (util/nil-or-loading? charts-data) chart-ids-loading?)
      [empty-state/loading-charts-card]

      (not-empty charts-data)
      [:div {:style {:display :flex
                     :flex-direction :column
                     :gap 12}}
       [l/section-header {}
        "Overall"]
       [session-count-chart charts-data]
       [vi.charts/chart-card {:chart-id "intents-by-value-counts"
                              :chart-type "BarChart"
                              :title "Intent Breakdown"
                              :name-key "label"
                              :data-keys ["value"]
                              :product "voice-insights"
                              :drill {:columns [{:column "label" :filter "tags" :tag "intent-summary"}]}
                              :required-tag-names ["intent-summary"]
                              :top-n-toggle 10
                              :show-value-mode-toggle? true
                              :add-listener-dispatch? true
                              :config-overrides {:margin {:top 20 :right 30 :left 20 :bottom 80}
                                                 :legend? false}}]
       [intent-resolution-chart charts-data]
       [intent-resolution-rate-chart charts-data]
       [intent-sentiment-sankey charts-data]
       [intent-sentiment-flow-chart charts-data]
       [improvement-recommendations-chart charts-data]
       [l/section-header {}
        "Tags"]
       [tag-distribution-by-day-chart charts-data]
       [overall-tag-distribution-chart charts-data]
       [tag-distribution-by-day-of-week-chart charts-data]
       [vi.charts/chart-card {:chart-id "churn-signal-by-value"
                              :chart-type "BarChart"
                              :title "Churn Signal"
                              :stack-id "time_value"
                              :name-key "time_value"
                              :key-for-data-keys "value_counts"
                              :required-tag-names ["churn-risk-signal"]
                              :show-time-period-toggle? true
                              :add-listener-dispatch? true
                              :config-overrides {:tooltip-config {:label-transf-fn util/->capitalize-words
                                                                  :show-percent? true}
                                                 :legend-config {:display-fn util/->capitalize-words}
                                                 :margin {:top 20 :right 30 :left 20 :bottom 0}
                                                 :legend? true}}]]

      :else
      [empty-state/no-charts-card])))
